import api from './api';
import { SystemUser, UserFormData, PaginatedResponse, ApiResponse, HmgUser } from '../types';

export const userService = {
  async getAll(page = 1, perPage = 15, search = ''): Promise<PaginatedResponse<SystemUser>> {
    const systemId = process.env.REACT_APP_SYSTEM_ID || '1';
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
      system_id: systemId,
      ...(search && { search }),
    });
    const response = await api.get<PaginatedResponse<HmgUser>>(`/hmg/users?${params}`);

    // Mapear HmgUser para SystemUser mantendo compatibilidade
    return {
      ...response.data,
      data: response.data.data.map((user: HmgUser) => ({
        id: user.id,
        name: user.name,
        nome: user.name,
        email: user.email,
        cpf: user.cpf,
        phone: user.phone,
        profile_id: user.hmg_role_id || 0,
        role_id: user.hmg_role_id,
        is_master: user.is_master,
        active: user.is_active,
        is_active: user.is_active,
      })),
    };
  },

  async getById(id: number): Promise<SystemUser> {
    const response = await api.get<HmgUser>(`/hmg/users/${id}`);
    const user = response.data;
    return {
      id: user.id,
      name: user.name,
      nome: user.name,
      email: user.email,
      cpf: user.cpf,
      phone: user.phone,
      profile_id: user.hmg_role_id || 0,
      role_id: user.hmg_role_id,
      is_master: user.is_master,
      active: user.is_active,
      is_active: user.is_active,
    };
  },

  async create(data: UserFormData): Promise<SystemUser> {
    const response = await api.post<HmgUser>('/hmg/users', {
      name: data.name,
      email: data.email,
      password: data.password,
      password_confirmation: data.password_confirmation,
      cpf: data.cpf,
      phone: data.phone,
      hmg_role_id: data.profile_id,
      system_id: Number(process.env.REACT_APP_SYSTEM_ID) || 1,
      is_active: data.active ?? true,
    });
    const user = response.data;
    return {
      id: user.id,
      name: user.name,
      nome: user.name,
      email: user.email,
      cpf: user.cpf,
      phone: user.phone,
      profile_id: user.hmg_role_id || 0,
      role_id: user.hmg_role_id,
      is_master: user.is_master,
      active: user.is_active,
      is_active: user.is_active,
    };
  },

  async update(id: number, data: UserFormData): Promise<SystemUser> {
    const response = await api.put<HmgUser>(`/hmg/users/${id}`, {
      name: data.name,
      email: data.email,
      ...(data.password && {
        password: data.password,
        password_confirmation: data.password_confirmation
      }),
      cpf: data.cpf,
      phone: data.phone,
      hmg_role_id: data.profile_id,
      is_active: data.active ?? true,
    });
    const user = response.data;
    return {
      id: user.id,
      name: user.name,
      nome: user.name,
      email: user.email,
      cpf: user.cpf,
      phone: user.phone,
      profile_id: user.hmg_role_id || 0,
      role_id: user.hmg_role_id,
      is_master: user.is_master,
      active: user.is_active,
      is_active: user.is_active,
    };
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/hmg/users/${id}`);
  },

  async toggleActive(id: number): Promise<SystemUser> {
    const response = await api.patch<ApiResponse<HmgUser>>(`/hmg/users/${id}/toggle-active`);
    const user = response.data.data!;
    return {
      id: user.id,
      name: user.name,
      nome: user.name,
      email: user.email,
      cpf: user.cpf,
      phone: user.phone,
      profile_id: user.hmg_role_id || 0,
      role_id: user.hmg_role_id,
      is_master: user.is_master,
      active: user.is_active,
      is_active: user.is_active,
    };
  },

  async resetPassword(id: number, newPassword: string): Promise<void> {
    await api.patch(`/hmg/users/${id}/reset-password`, {
      password: newPassword,
      password_confirmation: newPassword,
    });
  },
};
